import { jsonInit, toSearchParams } from '@shigo-ui/api-client';

import { createShigoApiClient, type ShigoApiConfig } from '../../lib/shigo-api-client';
import {
  DebugLookupStatus,
  debugLookupPageSchema,
  debugRuleSchema,
  debugRulesPageSchema,
  type DebugListFilter,
  type DebugRuleRequest,
} from './model';

interface DebugClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

interface DebugLookupRequest {
  searchTerm: string;
  selectedId?: string | undefined;
}

interface DebugPlacementLookupRequest extends DebugLookupRequest {
  publisherId?: string | undefined;
}

export function createDebugClient({ config, accessToken, fetcher }: DebugClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    list: (filter: DebugListFilter) =>
      api.request({
        path: `/api/debug?${serializeDebugListFilter(filter)}`,
        schema: debugRulesPageSchema,
      }),
    create: (payload: DebugRuleRequest) =>
      api.request({
        path: '/api/debug',
        schema: debugRuleSchema,
        init: jsonInit('POST', payload),
      }),
    update: (jobId: string, payload: DebugRuleRequest) =>
      api.request({
        path: `/api/debug/${encodeURIComponent(jobId)}`,
        schema: debugRuleSchema,
        init: jsonInit('PUT', payload),
      }),
    listPublishers: ({ searchTerm, selectedId }: DebugLookupRequest) =>
      api.request({
        path: `/api/publishers?${toSearchParams({
          page: 0,
          size: 25,
          name: searchTerm || undefined,
          ids: selectedId || undefined,
          status: DebugLookupStatus.Active,
        })}`,
        schema: debugLookupPageSchema,
      }),
    listPlacements: ({ searchTerm, selectedId, publisherId }: DebugPlacementLookupRequest) =>
      api.request({
        path: `/api/placements?${toSearchParams({
          page: 0,
          size: 25,
          searchTerm: searchTerm || undefined,
          ids: selectedId || undefined,
          publishersIds: publisherId || undefined,
          status: DebugLookupStatus.Active,
        })}`,
        schema: debugLookupPageSchema,
      }),
    listDemandPartners: ({ searchTerm, selectedId }: DebugLookupRequest) =>
      api.request({
        path: `/api/dps?${toSearchParams({
          page: 0,
          size: 25,
          displayName: searchTerm || undefined,
          ids: selectedId || undefined,
          status: [DebugLookupStatus.Active, DebugLookupStatus.Inactive],
        })}`,
        schema: debugLookupPageSchema,
      }),
  };
}

export function serializeDebugListFilter(filter: DebugListFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  }).toString();
}
